import axios from "axios";
import axiosInstance from "@/services/https/global";
import CONFIG from "@/config";

// ⚠️ CONFIRM THE SERVICE SEGMENT. The endpoint doc gives the paths from
// /api/v1/admin/account-closures onward with no service prefix. Whatever the
// merchant-facing close-account flow uses is almost certainly the same host.
const CLOSURE_SERVICE = "auth-service";

const baseUrl = `${CONFIG.BASE_URL}/${CLOSURE_SERVICE}/api/v1/admin/account-closures`;

// Same shape as the other services, with one addition: the HTTP status is
// preserved on the thrown object. 409 is load-bearing here (still blocked, or
// another admin already decided) and must be distinguishable from a generic
// failure even when the body omits statusCode.
const throwAxios = (error) => {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data;
    const base = typeof body === "object" && body !== null ? body : { message: body };
    const err = new Error(base.message ?? "Request failed");
    Object.assign(err, base, { statusCode: error.response?.status });
    return err;
  }
  throw new Error("An unexpected error occurred");
};


// `status` accepts a comma-separated list server-side (PENDING_REVIEW,APPROVED).
// `blocked` and `direction` are passed through as given.
export const getAccountClosures = async (params = {}) => {
  const queryParams = {};
  if (params.page !== undefined) queryParams.page = params.page;
  if (params.size !== undefined) queryParams.size = params.size;
  if (params.status) queryParams.status = params.status;
  if (params.blocked !== undefined && params.blocked !== "")
    queryParams.blocked = params.blocked;
  queryParams.direction = params.direction || "desc";
  if (params.search && params.search.trim())
    queryParams.search = params.search.trim();

  try {
    const response = await axiosInstance.get(baseUrl, { params: queryParams });
    return response.data;
  } catch (error) {
    throwAxios(error);
  }
};

// Re-checks the pending-actions panel server-side before responding, so this
// is the authority on whether approval is currently possible.
export const getAccountClosureById = async (id) => {
  try {
    const response = await axiosInstance.get(`${baseUrl}/${id}`);
    return response.data;
  } catch (error) {
    throwAxios(error);
  }
};

// 409 = refused. Either something is still open/unverifiable, or another admin
// already decided — in which case the standing decision comes back in `data`.
export const approveAccountClosure = async (id) => {
  try {
    const response = await axiosInstance.post(`${baseUrl}/${id}/approve`);
    return response.data;
  } catch (error) {
    throwAxios(error);
  }
};

// reason is mandatory, minimum 10 characters (CLOSURE_REJECT_REASON_MIN_LENGTH).
export const rejectAccountClosure = async ({ id, reason }) => {
  try {
    const response = await axiosInstance.post(`${baseUrl}/${id}/reject`, {
      reason,
    });
    return response.data;
  } catch (error) {
    throwAxios(error);
  }
};

// Read-only by design: every write verb on this path returns 403.
export const getClosureAudit = async ({ accountUserId, adminId } = {}) => {
  try {
    const response = await axiosInstance.get(`${baseUrl}/audit`, {
      params: {
        ...(accountUserId ? { accountUserId } : {}),
        ...(adminId ? { adminId } : {}),
      },
    });
    return response.data;
  } catch (error) {
    throwAxios(error);
  }
};

// Archived window only (day 60–90). ⚠️ Body shape is a guess — the endpoint doc
// gives no payload. Sending the business justification as { reason } to match
// the reject endpoint's convention.
export const supportTicketReopen = async ({ userId, reason }) => {
  try {
    const response = await axiosInstance.post(
      `${baseUrl}/accounts/${userId}/support-ticket-reopen`,
      { reason },
    );
    return response.data;
  } catch (error) {
    throwAxios(error);
  }
};